import { useQuery } from '@tanstack/react-query';
import {
  AssetClassSchema,
  AssetTypeSchema,
  DepartmentSchema,
  EmployeeSchema,
  ReferenceMasterSchema,
  type AssetClassDto,
  type AssetDto,
  type CustodyAssignmentDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { PersonName } from '../../shared/ui/person';
import { ProjectName } from '../crm/common';
import { RecordLink } from '../finance/detail';

/**
 * Pieces the asset pages share: the classes and types to choose from, the organization masters
 * that name a placement, and how a custodian reads (ERD §7.4).
 */

export const ASSETS = '/api/v1/assets/assets';
export const CLASSES = '/api/v1/assets/asset-classes';
export const CUSTODY = '/api/v1/assets/custody-assignments';

/** The states a manual status change can reach in Release 1 (FRD-AST-041). */
export const R1_STATUSES = ['active', 'in_storage', 'in_repair'] as const;

export const statusLabel = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');

/**
 * Asset classes, as configuration: every class, so an asset's class is always named. They are few
 * (FRD-AST-008 lists thirteen kinds) and read in one page.
 */
export function useAssetClasses() {
  const session = useSession();
  return useQuery({
    queryKey: ['assets', 'classes'],
    enabled: hasCapability(session.data, 'assets.asset_class:view'),
    queryFn: () => getPage(`${CLASSES}?pageSize=200&sort=code`, AssetClassSchema),
  });
}

/** A class as people read it: a sub-class names its class first ("IT › Laptops"). */
export function classLabel(classes: readonly AssetClassDto[], id: string | null): string {
  if (id === null) return '—';
  const found = classes.find((c) => c.id === id);
  if (found === undefined) return 'Not visible';
  const parent = classes.find((c) => c.id === found.parentClassId);
  return parent === undefined ? found.name : `${parent.name} › ${found.name}`;
}

/** Classes in tree order: each class followed by its sub-classes. */
export function classTree(
  classes: readonly AssetClassDto[],
): { row: AssetClassDto; depth: 0 | 1 }[] {
  const tops = classes.filter((c) => c.parentClassId === null);
  return tops.flatMap((top) => [
    { row: top, depth: 0 as const },
    ...classes.filter((c) => c.parentClassId === top.id).map((row) => ({ row, depth: 1 as const })),
  ]);
}

/** The values of the asset type list; retired ones label existing assets but are not offered. */
export function useAssetTypes() {
  return useQuery({
    queryKey: ['assets', 'types'],
    queryFn: () => getData('/api/v1/assets/asset-types', z.array(AssetTypeSchema)),
  });
}

/** Every department, cost centre and location the session may read, for naming a placement. */
export function useMasters() {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const departments = useQuery({
    queryKey: ['masters-all', 'departments'],
    enabled: can('organization.department:view'),
    queryFn: () =>
      getPage('/api/v1/organization/departments?pageSize=200&sort=code', DepartmentSchema),
  });
  const costCentres = useQuery({
    queryKey: ['masters-all', 'cost-centers'],
    enabled: can('organization.cost_center:view'),
    queryFn: () =>
      getPage('/api/v1/organization/cost-centers?pageSize=200&sort=code', ReferenceMasterSchema),
  });
  const locations = useQuery({
    queryKey: ['masters-all', 'locations'],
    enabled: can('organization.location:view'),
    queryFn: () =>
      getPage('/api/v1/organization/locations?pageSize=200&sort=code', ReferenceMasterSchema),
  });
  const name = (
    rows: readonly { id: string; code: string; name: string }[] | undefined,
    id: string | null,
  ) => {
    if (id === null) return '—';
    const found = rows?.find((row) => row.id === id);
    return found === undefined ? 'Not visible' : `${found.code} — ${found.name}`;
  };
  return {
    department: (id: string | null) => name(departments.data?.items, id),
    costCentre: (id: string | null) => name(costCentres.data?.items, id),
    location: (id: string | null) => name(locations.data?.items, id),
    departments: departments.data?.items ?? [],
    costCentres: costCentres.data?.items ?? [],
    locations: locations.data?.items ?? [],
    can: {
      departments: can('organization.department:view'),
      costCentres: can('organization.cost_center:view'),
      locations: can('organization.location:view'),
    },
  };
}

type Custody = Pick<
  CustodyAssignmentDto,
  'custodianType' | 'employeeId' | 'departmentId' | 'locationId' | 'projectId' | 'thirdPartyName'
>;

/** The current custody of an asset, in the shape a history row has. */
export function currentCustody(asset: AssetDto): Custody | null {
  if (asset.custodianType === null) return null;
  return {
    custodianType: asset.custodianType,
    employeeId: asset.custodianEmployeeId,
    departmentId: asset.custodianDepartmentId,
    locationId: asset.custodianLocationId,
    projectId: asset.custodianProjectId,
    thirdPartyName: asset.custodianThirdPartyName,
  };
}

/**
 * A custodian, named from its own record: an employee links to the employee's page where the
 * session may read employees, a project to the project's page where it may read projects; nothing
 * links to a record the session cannot open.
 */
export function Custodian({ custody }: { custody: Custody | null }) {
  const masters = useMasters();
  if (custody === null) return <>Not assigned</>;
  switch (custody.custodianType) {
    case 'employee':
      return <EmployeeName id={custody.employeeId ?? ''} />;
    case 'department':
      return <>Department {masters.department(custody.departmentId)}</>;
    case 'location':
      return <>Location {masters.location(custody.locationId)}</>;
    case 'project':
      return (
        <>
          Project <ProjectName id={custody.projectId} />
        </>
      );
    case 'pool':
      return <>Pool (unassigned stock)</>;
    case 'third_party':
      return <>Third party: {custody.thirdPartyName}</>;
  }
}

function EmployeeName({ id }: { id: string }) {
  const session = useSession();
  const canSee = hasCapability(session.data, 'hr.employee:view');
  const employee = useQuery({
    queryKey: ['hr', 'employee', id],
    enabled: canSee,
    retry: false,
    queryFn: () => getData(`/api/v1/hr/employees/${id}`, EmployeeSchema),
  });
  if (!canSee) return <>Employee</>;
  if (employee.isPending) return <>…</>;
  if (employee.data === undefined) return <>Employee (not visible)</>;
  return (
    <PersonName name={employee.data.name} employeeId={id}>
      <RecordLink to={`/hr/employees/${id}`}>
        {employee.data.code} — {employee.data.name}
      </RecordLink>
    </PersonName>
  );
}

/** The masters a new choice may use: the active ones, plus the one a record already holds. */
export function selectable<T extends { id: string; status: string }>(
  rows: readonly T[],
  current: string,
): T[] {
  return rows.filter((row) => row.status === 'active' || row.id === current);
}

/** Today in the business time zone, as a date field value (YYYY-MM-DD). */
export function businessToday(timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone ?? 'Asia/Dubai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
